import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Button } from './Button'
import { Modal } from './Modal'

export type StatusModalVariant = 'success' | 'error' | 'info'

export type StatusModalProps = {
  open: boolean
  onClose: () => void
  variant?: StatusModalVariant
  title: string
  description?: ReactNode
  actionLabel?: string
  autoCloseMs?: number
}

function IconCheck({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

function IconAlert({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  )
}

function IconInfo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  )
}

const variantStyles: Record<
  StatusModalVariant,
  { ring: string; iconBg: string; icon: string; Icon: typeof IconCheck }
> = {
  success: {
    ring: 'from-emerald-500/20 via-emerald-400/10 to-transparent',
    iconBg: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    icon: 'h-7 w-7',
    Icon: IconCheck,
  },
  error: {
    ring: 'from-destructive/25 via-destructive/10 to-transparent',
    iconBg: 'bg-destructive/15 text-destructive',
    icon: 'h-7 w-7',
    Icon: IconAlert,
  },
  info: {
    ring: 'from-primary/20 via-primary/10 to-transparent',
    iconBg: 'bg-primary/15 text-primary',
    icon: 'h-7 w-7',
    Icon: IconInfo,
  },
}

export function StatusModal({
  open,
  onClose,
  variant = 'success',
  title,
  description,
  actionLabel = 'Done',
  autoCloseMs,
}: StatusModalProps) {
  const styles = variantStyles[variant]
  const { Icon } = styles

  useEffect(() => {
    if (!open || !autoCloseMs || variant !== 'success') return
    const id = window.setTimeout(() => onClose(), autoCloseMs)
    return () => window.clearTimeout(id)
  }, [open, autoCloseMs, variant, onClose])

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <Button type="button" className="w-full sm:w-auto" onClick={onClose}>
          {actionLabel}
        </Button>
      }
    >
      <div
        className="flex flex-col items-center gap-4 pb-1 text-center text-foreground"
        aria-live={variant === 'success' ? 'polite' : undefined}
      >
        <div
          className={cn(
            'relative flex h-16 w-16 items-center justify-center rounded-full',
            styles.iconBg,
          )}
        >
          <div
            className={cn(
              'pointer-events-none absolute inset-0 rounded-full bg-gradient-to-br opacity-80',
              styles.ring,
            )}
            aria-hidden
          />
          <Icon className={styles.icon} />
        </div>
        {description ? (
          <p className="max-w-sm text-base leading-relaxed text-foreground">{description}</p>
        ) : null}
      </div>
    </Modal>
  )
}

type StatusModalOpenOptions = {
  title: string
  description?: ReactNode
  actionLabel?: string
  autoCloseMs?: number
}

type UseStatusModalReturn = {
  openSuccess: (options: StatusModalOpenOptions) => void
  openError: (options: StatusModalOpenOptions) => void
  openInfo: (options: StatusModalOpenOptions) => void
  closeStatusModal: () => void
  statusModalProps: StatusModalProps
}

const closedState: StatusModalProps = {
  open: false,
  onClose: () => {},
  variant: 'success',
  title: '',
}

export function useStatusModal(): UseStatusModalReturn {
  const [state, setState] = useState<StatusModalProps>(closedState)

  const closeStatusModal = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }))
  }, [])

  const open = useCallback(
    (variant: StatusModalVariant, options: StatusModalOpenOptions) => {
      setState({
        open: true,
        onClose: closeStatusModal,
        variant,
        title: options.title,
        description: options.description,
        actionLabel: options.actionLabel,
        autoCloseMs: options.autoCloseMs,
      })
    },
    [closeStatusModal],
  )

  const openSuccess = useCallback(
    (options: StatusModalOpenOptions) =>
      open('success', { autoCloseMs: 4000, ...options }),
    [open],
  )

  const openError = useCallback(
    (options: StatusModalOpenOptions) => open('error', options),
    [open],
  )

  const openInfo = useCallback(
    (options: StatusModalOpenOptions) => open('info', options),
    [open],
  )

  return {
    openSuccess,
    openError,
    openInfo,
    closeStatusModal,
    statusModalProps: { ...state, onClose: closeStatusModal },
  }
}
